import { useState } from 'react';
import {
  Banner,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Icon,
  PageLoader,
  Panel,
  PanelSection,
  Paragraph,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { type Plan, Plans, type PlanType } from '@/services/billing/plans';
import { ContractsSalesDrawer } from '../contracts-sales/components/contracts-sales-drawer';
import { CtaButton } from './cta-button';
import {
  useAvailablePlansLoader,
  useAvailablePlansModel,
  type AvailablePlansModel,
} from '../hooks/use-available-plans';
import { useAvailablePlansAnalytics } from '../hooks/use-available-plans-analytics';
import { StartupPlanInfo } from './startup-plan-info';
import { findOrdwayPlan } from '../utils/find-ordway-plan';
import { CHANGE_PLAN_COLUMNS, CHANGE_PLAN_ROWS, type ChangePlanColumn } from './change-plan-rows';
import * as planStyles from './plan-info.css';
import * as styles from './change-plan-grid.css';

type OnCtaClick = (text: string, planType: PlanType) => void;

/** Whether the account is subscribed to (or pending checkout for) this plan. */
function isSubscribedToPlan(planType: PlanType, model: AvailablePlansModel): boolean {
  const active = model.hasActiveSubscription && model.currentPlanType === planType;
  const pending = model.isPendingCheckout && model.futurePlanType === planType;
  return active || pending;
}

/** A cell class that tints the column when it holds the current plan. */
function cellClassName(base: string, isCurrent: boolean): string {
  return isCurrent ? `${base} ${styles.currentColumn}` : base;
}

/** The non-interactive "Your plan" marker, shown in the current plan's column in place of a CTA. */
function YourPlanMarker() {
  return (
    <Box as="span" className={planStyles.yourPlanBadge} data-test-selected-plan-label>
      <Icon name="check" size="sm" />
      Your plan
    </Box>
  );
}

type ColumnHeaderProps = {
  column: ChangePlanColumn;
  plan: Plan;
  model: AvailablePlansModel;
  isCurrent: boolean;
  onLetsTalk: (planType: PlanType) => void;
  onCtaClick: OnCtaClick;
};

function ColumnHeader({ column, plan, model, isCurrent, onLetsTalk, onCtaClick }: ColumnHeaderProps) {
  return (
    <Box className={cellClassName(styles.headerCell, isCurrent)} role="columnheader">
      <Stack gap="150">
        <Heading level="3" size="sm" weight="bold">
          {column.name}
        </Heading>
        <Box>
          <Text size="xxxl" weight="bold">
            {column.priceLabel}
          </Text>{' '}
          <Text color="secondary">{column.priceSuffix}</Text>
        </Box>
        <Paragraph variant="secondary">{column.description}</Paragraph>
        {/* The CTA hugs its label; a block wrapper keeps the button at its intrinsic width. */}
        <Box>
          {isCurrent ? (
            <YourPlanMarker />
          ) : (
            <CtaButton
              plan={plan}
              ordwayPlan={findOrdwayPlan(model.ordwayPlans, plan)}
              model={model}
              onLetsTalk={onLetsTalk}
              onCtaClick={onCtaClick}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

type ChangePlanSalesCardProps = {
  isReadOnly: boolean;
  onTalkToSales: () => void;
};

/** The full-width card beneath the grid, for needs the two plans do not cover. */
function ChangePlanSalesCard({ isReadOnly, onTalkToSales }: ChangePlanSalesCardProps) {
  return (
    <Panel data-test-change-plan-sales-card>
      <PanelSection>
        <Flex justifyContent="space-between" alignItems="center" gap="300">
          <Stack gap="100">
            <Heading level="3" size="sm" weight="bold">
              Custom and enterprise options
            </Heading>
            <Paragraph variant="secondary">
              Negotiated volume and rates, a dedicated CSM, security review, custom contract terms, and anything else
              your team needs. Our sales team will put together a plan for you.
            </Paragraph>
          </Stack>
          <Button variant="primary" isDisabled={isReadOnly} onClick={onTalkToSales}>
            Talk to sales
          </Button>
        </Flex>
      </PanelSection>
    </Panel>
  );
}

type ChangePlanComparisonProps = {
  model: AvailablePlansModel;
  onCtaClick: OnCtaClick;
};

/**
 * The Change plan comparison: two columns (Essentials, Premium) over a static feature grid, plus a
 * full-width sales card. Priced header values come from the `Plans` catalog; the feature rows are
 * static copy because the API serves feature limits for the current account only. Presentational: the
 * billing state arrives as `model`, so tests drive it directly.
 */
export function ChangePlanComparison({ model, onCtaClick }: ChangePlanComparisonProps) {
  // The plan whose sales sidebar is open: Premium from its "Let's talk" CTA, Enterprise from the sales card.
  const [salesPlan, setSalesPlan] = useState<PlanType | null>(null);

  const essentialsIsCurrent = isSubscribedToPlan(Plans.Essentials.planType, model);
  const premiumIsCurrent = isSubscribedToPlan(Plans.Premium.planType, model);

  const openSales = (plan: PlanType) => {
    onCtaClick("Let's Talk", plan);
    setSalesPlan(plan);
  };

  return (
    <Stack gap="400">
      <Box className={styles.container} data-test-change-plan-grid>
        <Box className={styles.grid} role="table" aria-label="Plan comparison">
          <Box className={styles.row} role="row">
            {/* The empty top-left corner keeps the header row at three columns. */}
            <Box className={styles.headerCell} role="columnheader" />
            <ColumnHeader
              column={CHANGE_PLAN_COLUMNS.essentials}
              plan={Plans.Essentials}
              model={model}
              isCurrent={essentialsIsCurrent}
              onLetsTalk={setSalesPlan}
              onCtaClick={onCtaClick}
            />
            <ColumnHeader
              column={CHANGE_PLAN_COLUMNS.premium}
              plan={Plans.Premium}
              model={model}
              isCurrent={premiumIsCurrent}
              onLetsTalk={setSalesPlan}
              onCtaClick={onCtaClick}
            />
          </Box>
          {CHANGE_PLAN_ROWS.map((row) => (
            <Box className={styles.row} role="row" key={row.label}>
              <Box className={styles.labelCell} role="rowheader">
                <Text>{row.label}</Text>
              </Box>
              <Box className={cellClassName(styles.valueCell, essentialsIsCurrent)} role="cell">
                <Text color="secondary">{row.essentials}</Text>
              </Box>
              <Box className={cellClassName(styles.valueCell, premiumIsCurrent)} role="cell">
                <Text color="secondary">{row.premium}</Text>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <ChangePlanSalesCard isReadOnly={model.isReadOnly} onTalkToSales={() => openSales(Plans.Enterprise.planType)} />

      <ContractsSalesDrawer
        isOpen={salesPlan != null}
        planClicked={salesPlan ?? undefined}
        onClose={() => setSalesPlan(null)}
      />
    </Stack>
  );
}

/**
 * The Change plan tab body: loads the Ordway plan catalog (for the checkout link), shows a page loader
 * or an error banner while it settles, then renders the {@link ChangePlanComparison}.
 */
export function ChangePlanGrid() {
  const model = useAvailablePlansModel();
  const loader = useAvailablePlansLoader();
  const analytics = useAvailablePlansAnalytics();

  if (loader.isLoading) {
    return (
      <Grid py="2000">
        <PageLoader label="Loading plans…" />
      </Grid>
    );
  }

  if (loader.isError) {
    return (
      <Banner
        variant="critical"
        title="Couldn't load plans"
        description="Something went wrong loading the plans."
        action={{ label: 'Try again', onClick: loader.retry }}
      />
    );
  }

  return (
    <>
      <ChangePlanComparison model={model} onCtaClick={analytics.trackCtaClicked} />
      {model.showStartupPlans ? <StartupPlanInfo onTrackClick={analytics.trackStartupCardClicked} /> : null}
    </>
  );
}
